import React, {useState} from 'react';
import styled from 'styled-components';
import {useQueryParams, StringParam} from 'use-query-params';
import {useFormik} from 'formik';
import * as yup from 'yup';
import {Form, Input, Message} from 'semantic-ui-react';
import {useHistory} from 'react-router-dom';
import {useSignupMutation} from 'api';
import {PositiveButton} from '../../../components/mypage/MypageLayout';

const validateSchema = yup.object().shape({
  email: yup.string().trim().required('メールアドレスは必須項目です'),
  lastName: yup.string().trim().required('姓は必須項目です'),
  firstName: yup.string().trim().required('名は必須項目です'),
  tel: yup.string().trim().required('TELは必須項目です'),
  password: yup
    .string()
    .trim()
    .min(6, 'パスワードは６文字以上で設定してください。')
    .required('パスワードを入力してください'),
  passwordConfirmation: yup
    .string()
    .trim()
    .required('パスワード(確認)を入力してください')
    .oneOf([yup.ref('password'), null], 'パスワードが一致していません'),
});

const SignupForm = () => {
  const [query] = useQueryParams({
    email: StringParam,
    firstName: StringParam,
    lastName: StringParam,
  });
  const {email = '', firstName = '', lastName = ''} = query;

  const [agree, setAgree] = useState(false);
  const history = useHistory();
  const [signup, {data, loading}] = useSignupMutation({
    onCompleted: (data) => {
      if (!data?.signup?.error) {
        history.push('/signup/success');
      }
    },
  });

  const formik = useFormik({
    validationSchema: validateSchema,
    initialValues: {
      email,
      firstName,
      lastName,
      tel: '',
      password: '',
      passwordConfirmation: '',
    },
    onSubmit: (values) => {
      signup({
        variables: {
          user: {
            email: values.email,
            firstName: values.firstName,
            lastName: values.lastName,
            tel: values.tel,
          },
          password: {
            password: values.password,
            passwordConfirmation: values.passwordConfirmation,
          },
        },
      });
    },
  });

  return (
    <Container>
      <Title>新規登録</Title>

      {data?.signup?.error && (
        <Message negative>
          <p>{data.signup.error}</p>
        </Message>
      )}

      <Form onSubmit={formik.handleSubmit}>
        <Form.Field required className="field">
          <label>メールアドレス</label>
          <Input
            name="email"
            type="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          {formik.touched.email && formik.errors.email && (
            <Message negative>{formik.errors.email}</Message>
          )}
        </Form.Field>

        <Form.Field required className="field">
          <label>姓</label>
          <Input
            name="lastName"
            type="text"
            value={formik.values.lastName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          {formik.touched.lastName && formik.errors.lastName && (
            <Message negative>{formik.errors.lastName}</Message>
          )}
        </Form.Field>

        <Form.Field required className="field">
          <label>名</label>
          <Input
            name="firstName"
            type="text"
            value={formik.values.firstName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          {formik.touched.firstName && formik.errors.firstName && (
            <Message negative>{formik.errors.firstName}</Message>
          )}
        </Form.Field>

        <Form.Field required className="field">
          <label>TEL</label>
          <Input
            name="tel"
            type="text"
            value={formik.values.tel}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          {formik.touched.tel && formik.errors.tel && (
            <Message negative>{formik.errors.tel}</Message>
          )}
        </Form.Field>

        <Form.Field required className="field">
          <label>パスワード</label>
          <Input
            name="password"
            type="password"
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          {formik.touched.password && formik.errors.password && (
            <Message negative>{formik.errors.password}</Message>
          )}
        </Form.Field>

        <Form.Field required className="field">
          <label>パスワード(確認)</label>
          <Input
            name="passwordConfirmation"
            type="password"
            value={formik.values.passwordConfirmation}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          {formik.touched.passwordConfirmation &&
            formik.errors.passwordConfirmation && (
              <Message negative>{formik.errors.passwordConfirmation}</Message>
            )}
        </Form.Field>

        <div className="policy">
          <label className="terms">
            <input
              type="checkbox"
              onChange={(e) => {
                setAgree(e.target.checked);
              }}
              checked={agree}
            />
            <a href="/terms" target="_blank">
              利用規約
            </a>
            と
            <a href="/privacy" target="_blank">
              プライバシーポリシー
            </a>
            に同意する
          </label>
        </div>

        <Actions>
          <PositiveButton
            positive
            type="submit"
            size="large"
            loading={loading}
            disabled={!agree || loading || !formik.isValid}>
            メールアドレスで登録
          </PositiveButton>
        </Actions>
      </Form>
    </Container>
  );
};

export {SignupForm};

const Container = styled.div`
  padding-top: 90px;
  text-align: center;
  width: 400px;
  margin: 0 auto;
  margin-bottom: 90px;

  .description {
    margin-top: 20px;
    font-size: 12px;
    line-height: 1.5;
    color: #2c2c2c;

    p {
      margin-bottom: 0;
    }
  }

  .border {
    font-size: 13px;
    margin: 40px 0;
    height: 1px;
    border-top: solid 1px #999999;
    position: relative;

    p {
      position: absolute;
      top: -10px;
      left: 39%;
      background-color: #fff;
      width: 80px;
      text-align: center;
    }
  }

  label {
    text-align: left;
    opacity: 0.37;
    color: #333333;
    font-size: 12px;
  }

  input[type='email'],
  input[type='password'] {
    display: block;
    margin: 0 auto;
    width: 100%;
    height: 45px;
    background-color: #f5f5f5;
    border: solid 1px #f1f1f1;
  }

  .policy {
    label {
      display: block;
      text-align: left;
      opacity: 0.7;
      color: #333333;
      font-size: 12px;

      &:hover {
        cursor: pointer;
      }

      input[type='checkbox'] {
        margin-top: 3px;
        margin-right: 5px;
      }

      &.terms {
        opacity: 1;
      }
    }
  }
`;

const Title = styled.h1`
  margin-bottom: 70px;
  font-size: 26px;
  font-weight: 500;
  text-align: center;
  color: #666666;
`;

const Facebook = styled.a`
  &&& {
    display: block;
    margin: 0 auto;
    width: 100%;
    height: 56px;
    line-height: 56px;
    background-color: #305097;
    color: #fff;
    font-size: 17px;
    letter-spacing: 0.5px;
    text-align: center;
    text-decoration: none;
    border-radius: 72.5px;

    &:hover {
      color: #fff;
      background-color: #4d6fbb;
      text-decoration: none;
      cursor: pointer;
    }

    i {
      margin-right: 10px;
    }
  }
`;

const Actions = styled.div`
  margin: 60px 0;
`;
